
import { useEffect, useCallback, useRef } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { ScoresState, TendenciesState } from "./types";

interface AutoSaveData {
  selectedTest: string;
  scores: ScoresState;
  ballTendencies: TendenciesState;
  currentPoint: string;
  currentAttempt: number;
  savedAt: string;
}

interface AutoSaveProps {
  selectedTest: string | null;
  scores: ScoresState;
  ballTendencies: TendenciesState;
  currentPoint: string;
  currentAttempt: number;
  setScores: (scores: ScoresState) => void;
  setSelectedTest: (test: string | null) => void;
  setCurrentPoint: (point: string) => void;
  setCurrentAttempt: (attempt: number) => void;
  setBallTendencies: (tendencies: TendenciesState) => void;
}

const getStorageKey = (): string => {
  const pathname = window.location.pathname;
  if (pathname.includes('level1')) {
    return 'level1_current_exercise';
  } else if (pathname.includes('level2')) {
    return 'level2_current_exercise';
  }
  return 'level3_current_exercise';
};

// Fonction utilitaire pour vérifier si des données sauvegardées existent
export const hasSavedExerciseData = (): { hasData: boolean; testId: string | null } => {
  try {
    const storageKey = getStorageKey();
    const savedData = localStorage.getItem(storageKey);
    
    if (savedData) {
      const parsed: AutoSaveData = JSON.parse(savedData);
      
      if (parsed.selectedTest && parsed.scores) {
        const hasRealScores = Object.values(parsed.scores).some(
          pointScores => Array.isArray(pointScores) && pointScores.some(s => s !== null)
        );
        
        if (hasRealScores) {
          return { hasData: true, testId: parsed.selectedTest };
        }
      }
    }
  } catch (err) {
    console.error("Erreur lors de la vérification des données sauvegardées:", err);
  }
  return { hasData: false, testId: null };
};

/**
 * Hook qui gère la sauvegarde automatique des données d'exercice dans localStorage
 * et restaure les données au chargement
 */
export const useAutoSave = ({
  selectedTest,
  scores,
  ballTendencies,
  currentPoint,
  currentAttempt,
  setScores,
  setSelectedTest,
  setCurrentPoint,
  setCurrentAttempt,
  setBallTendencies
}: AutoSaveProps) => {
  const { toast } = useToast();
  const { t } = useTranslation('common');
  const hasRestoredRef = useRef(false);
  const isRestoringRef = useRef(false);
  const latestDataRef = useRef<AutoSaveData | null>(null);

  const saveNow = useCallback(() => {
    if (isRestoringRef.current) return;
    if (!latestDataRef.current) return;

    try {
      const storageKey = getStorageKey();
      localStorage.setItem(storageKey, JSON.stringify(latestDataRef.current));
      // eslint-disable-next-line no-console
      console.log(`Données sauvegardées (${storageKey})`);
    } catch (err) {
      console.error("Erreur lors de la sauvegarde dans localStorage:", err);
      toast({
        title: t('toasts.exerciseSaveFailed.title'),
        description: t('toasts.exerciseSaveFailed.description'),
        variant: 'destructive'
      });
    }
  }, [toast, t]);

  // Chargement et restauration des données depuis localStorage au démarrage
  useEffect(() => {
    // Éviter la double restauration
    if (hasRestoredRef.current || isRestoringRef.current) return;

    const restoreData = () => {
      try {
        const storageKey = getStorageKey();
        const savedData = localStorage.getItem(storageKey);

        if (savedData) {
          const parsed: AutoSaveData = JSON.parse(savedData);

          if (parsed.selectedTest && parsed.scores && Object.keys(parsed.scores).length > 0) {
            const hasRealScores = Object.values(parsed.scores).some(
              pointScores => Array.isArray(pointScores) && pointScores.some(s => s !== null)
            );

            if (hasRealScores) {
              console.log(`Restauration des données sauvegardées (${storageKey}):`, parsed);
              isRestoringRef.current = true;
              hasRestoredRef.current = true;

              // Restaurer tous les états
              setScores(parsed.scores);
              setCurrentPoint(parsed.currentPoint);
              setCurrentAttempt(parsed.currentAttempt);
              if (parsed.ballTendencies) {
                setBallTendencies(parsed.ballTendencies);
              }
              setSelectedTest(parsed.selectedTest);

              // Afficher une notification
              setTimeout(() => {
                toast({
                  title: t('toasts.exerciseRestored.title'),
                  description: t('toasts.exerciseRestored.description'),
                });
                isRestoringRef.current = false;
              }, 100);

              return true;
            } else {
              localStorage.removeItem(storageKey);
            }
          }
        }
      } catch (err) {
        console.error("Erreur lors du chargement des données sauvegardées:", err);
        const storageKey = getStorageKey();
        localStorage.removeItem(storageKey);
      }
      return false;
    };

    restoreData();
  }, []);

  // Mettre à jour la "photo" des données à sauvegarder, et sauvegarder à chaque changement
  useEffect(() => {
    if (!selectedTest) return;

    const hasRealScores = Object.values(scores).some(
      pointScores => Array.isArray(pointScores) && pointScores.some(s => s !== null)
    );

    if (!hasRealScores) return;

    latestDataRef.current = {
      selectedTest,
      scores,
      ballTendencies,
      currentPoint,
      currentAttempt,
      savedAt: new Date().toISOString()
    };

    saveNow();
  }, [selectedTest, scores, ballTendencies, currentPoint, currentAttempt, saveNow]);

  // Sauvegarde "d'urgence" sur mobile quand l'app passe en arrière-plan / écran verrouillé
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        saveNow();
      }
    };

    const onPageHide = () => {
      saveNow();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [saveNow]);

  const clearLocalStorage = useCallback(() => {
    const storageKey = getStorageKey();
    localStorage.removeItem(storageKey);
    hasRestoredRef.current = false;
    latestDataRef.current = null;
    console.log(`Données supprimées (${storageKey})`);
  }, []);

  return { clearLocalStorage, hasRestored: hasRestoredRef.current };
};
